import React from 'react';
import { motion } from 'framer-motion';
import { VARIANTS, clipLeft, useMotionOK, withDelay, type VariantName } from '../motion';

const VIEWPORT = { once: true, margin: '-10% 0px -10% 0px' } as const;

/* A finished write-on must leave no clip behind: rough-notation draws its mark
   after the line has landed, as an absolutely positioned SVG inside this box,
   and any surviving inset() crops it. */
function useClipRelease(active: boolean) {
    const ref = React.useRef<HTMLElement>(null);
    const onAnimationComplete = React.useCallback(() => {
        if (active && ref.current) ref.current.style.clipPath = 'none';
    }, [active]);
    return { ref, onAnimationComplete };
}

type Tag = 'div' | 'span' | 'p' | 'li' | 'ul' | 'ol' | 'h2' | 'section';

type Props = {
    variant?: VariantName;
    delay?: number;
    as?: Tag;
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
};

/* Animates the element itself rather than a wrapper, so grids, flex rows and
   baseline alignment stay exactly as authored. `In` fires on scroll, `Beat`
   fires on mount for the hero's opening sequence. */
export function In({ variant = 'fadeUp', delay = 0, as = 'div', className, style, children }: Props) {
    const ok = useMotionOK();
    const clip = useClipRelease(variant === 'clipLeft');
    const Tag = motion[as] as React.ElementType;
    return (
        <Tag
            ref={clip.ref}
            onAnimationComplete={clip.onAnimationComplete}
            className={className}
            style={style}
            variants={withDelay(VARIANTS[variant], delay)}
            initial={ok ? 'hidden' : false}
            whileInView="shown"
            viewport={VIEWPORT}
        >
            {children}
        </Tag>
    );
}

export function Beat({ variant = 'fadeUp', delay = 0, as = 'div', className, style, children }: Props) {
    const ok = useMotionOK();
    const clip = useClipRelease(variant === 'clipLeft');
    const Tag = motion[as] as React.ElementType;
    return (
        <Tag
            ref={clip.ref}
            onAnimationComplete={clip.onAnimationComplete}
            className={className}
            style={style}
            variants={withDelay(VARIANTS[variant], delay)}
            initial={ok ? 'hidden' : false}
            animate="shown"
        >
            {children}
        </Tag>
    );
}

/* Every handwritten kicker writes on the same way, in the same direction, for
   the same 420ms. */
export function Kicker({
    tilt,
    delay = 0,
    onMount = false,
    children,
}: {
    tilt: string;
    delay?: number;
    /* Hero kickers run off the page-load clock, not the scroll position. */
    onMount?: boolean;
    children: React.ReactNode;
}) {
    const ok = useMotionOK();
    const clip = useClipRelease(true);
    const trigger = onMount ? { animate: 'shown' as const } : { whileInView: 'shown' as const, viewport: VIEWPORT };
    return (
        <motion.span
            ref={clip.ref as React.Ref<HTMLSpanElement>}
            onAnimationComplete={clip.onAnimationComplete}
            className="ds-kicker"
            style={{ '--tilt': tilt } as React.CSSProperties}
            variants={withDelay(clipLeft, delay)}
            initial={ok ? 'hidden' : false}
            {...trigger}
        >
            {children}
        </motion.span>
    );
}
